// src/pages/UpdatedHomePage.tsx
import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { motion, useReducedMotion } from 'framer-motion';

import { Navbar, NavbarSpacer } from '@/components/ui/navbar';
import { SidebarLayout } from '@/components/layout/SidebarLayout';
import { auth, logout } from '@/lib/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/app/providers/AuthProvider';
import { useMaintenance } from '@/app/providers/MaintenanceProvider';
import { canAccessPlatformAdmin } from '@/components/auth/platformAdminAccess';
import { useTheme } from '@/app/providers/ThemeProvider';
import { useToast } from '@/app/providers/ToastProvider';
import DebugPanel from '@/components/DebugPanel';
import SupportModal from '@/components/SupportModal';
import GuideModal from '@/components/GuideModal';
import ShortcutHelpModal from '@/components/ShortcutHelpModal';
import ReleaseNotesCenter from '@/components/ReleaseNotesCenter';
import { useReleaseNotes } from '@/hooks/useReleaseNotes';
import { isKnownHomeSection, renderHomeContent } from '@/app/router/renderHomeContent';
import { normalizeMaintenancePathname, resolveMaintenanceHub } from '@/app/router/maintenanceHubMap';
import NotFound from '@/pages/NotFound';
import AppSidebar from '@/components/navigation/AppSidebar';
import { isAdminRoute } from '@/components/navigation/adminNavigation';
import { DOCUMENTS_HUB_ENABLED } from '@/config/featureFlags';
import { getVisibleHomeHubs } from './homeHubVisibility';

import {
  ArrowUpRightIcon,
  BeakerIcon,
  BriefcaseIcon,
  ClockIcon,
  CommandLineIcon,
  ShoppingBagIcon,
  UserGroupIcon,
} from '@heroicons/react/20/solid';
import { ADMIN_SERVICE_URL } from '@/config/endpoints';

// Messaggi motivazionali randomici per la home (UNI/PdR 125 compliant - sociale + tecnologia)
const welcomeMessages = [
  { greeting: 'Buongiorno', message: 'Dietro ogni dato c\'è una persona, dietro ogni numero una storia da valorizzare.' },
  { greeting: 'Salve', message: 'La tecnologia al servizio dell\'umanità: dati per costruire un welfare più giusto.' },
  { greeting: 'Ciao', message: 'Innovazione sociale significa misurare l\'impatto per amplificarlo.' },
  { greeting: 'Buongiorno', message: 'Dai numeri alle persone: ogni KPI racconta il cambiamento che generiamo.' },
  { greeting: 'Salve', message: 'Trasformare i dati in cura: la tecnologia che abbraccia la fragilità.' },

  { greeting: 'Buongiorno', message: 'L\'inclusione si misura, la dignità si protegge: dati per un impatto reale.' },
  { greeting: 'Ciao', message: 'Ogni dashboard è una finestra sul cambiamento sociale che vogliamo vedere.' },
  { greeting: 'Salve', message: 'La cooperazione incontra l\'innovazione: insieme per un futuro più equo.' },

  { greeting: 'Buongiorno', message: 'Non solo numeri, ma vite trasformate: misuriamo ciò che conta davvero.' },
  { greeting: 'Ciao', message: 'Tecnologia con un\'anima: i dati illuminano il cammino verso l\'inclusione.' },
  { greeting: 'Salve', message: 'Dall\'analisi all\'azione: ogni insight è un\'opportunità per fare la differenza.' },

  { greeting: 'Buongiorno', message: 'Il welfare del futuro nasce dall\'incontro tra cuore e intelligenza dei dati.' },
  { greeting: 'Salve', message: 'Rendere visibile l\'invisibile: la forza dei dati per chi è ai margini.' },
  { greeting: 'Ciao', message: 'Ogni trend racconta un bisogno, ogni grafico una comunità da sostenere.' },

  { greeting: 'Buongiorno', message: 'Innovazione responsabile: la tecnologia che amplifica la missione sociale.' },
  { greeting: 'Salve', message: 'Dai dati alla dignità: misurare per prendersi cura meglio.' },
  
  { greeting: 'Ciao', message: 'L\'impatto sociale si costruisce un dato alla volta, una scelta alla volta.' },
  { greeting: 'Buongiorno', message: 'Trasparenza radicale, cura autentica: i numeri al servizio delle persone.' },
  { greeting: 'Salve', message: 'Dove la tecnologia incontra l\'empatia nascono servizi che cambiano vite.' },

  { greeting: 'Buongiorno', message: 'Ogni analisi è un atto di responsabilità verso chi serve la nostra comunità.' },
  { greeting: 'Ciao', message: 'I dati non mentono, le persone contano: questa è la nostra bussola.' },

  { greeting: 'Salve', message: 'Misurare l\'impatto sociale non è un obbligo, è un privilegio.' },
  { greeting: 'Buongiorno', message: 'La rivoluzione gentile: dati che guidano, valori che ispirano.' },
  { greeting: 'Ciao', message: 'Costruiamo ponti tra numeri e narrazioni, tra efficienza e umanità.' },

  { greeting: 'Salve', message: 'Ogni dashboard è un manifesto: crediamo in un welfare data-driven e human-centered.' },
  { greeting: 'Buongiorno', message: 'L\'innovazione sociale inizia qui: dove i dati incontrano la compassione.' },
  { greeting: 'Ciao', message: 'Non solo performance, ma trasformazione: misuriamo il cambiamento che vogliamo essere.' }
];

const homeHubs = [
  {
    id: 'business',
    title: 'Business',
    description: 'Controllo di gestione e processi aziendali',
    path: '/business',
    icon: BriefcaseIcon,
    accent: 'coral',
  },
  {
    id: 'dipendenze',
    title: 'Dipendenze',
    description: 'Servizi, assistiti e percorsi terapeutici',
    path: '/dipendenze',
    icon: BeakerIcon,
    accent: 'cyan',
  },
  {
    id: 'applicativi',
    title: 'Applicativi',
    description: 'Strumenti e servizi per il lavoro quotidiano',
    path: '/applicativi',
    icon: ShoppingBagIcon,
    accent: 'amber',
  },
  {
    id: 'hr',
    title: 'HR',
    description: 'Soci, dipendenti e amministrazione dei cedolini',
    path: '/hr',
    icon: UserGroupIcon,
    accent: 'violet',
  },
] as const;

type HomeHubId = typeof homeHubs[number]['id'];
type HomeHubAccent = typeof homeHubs[number]['accent'];

const homeHubStyles = {
  coral: {
    card: 'bg-rose-50/85 ring-rose-200/80 hover:bg-rose-100 hover:ring-rose-300 dark:bg-[#ef6351]/[0.08] dark:ring-white/[0.08] dark:hover:bg-[#ef6351]/[0.16] dark:hover:ring-[#ff9d91]/45',
    icon: 'bg-rose-100 text-rose-700 ring-rose-200/80 dark:bg-[#ef6351]/10 dark:text-[#ffb0a6] dark:ring-[#ff9d91]/15 dark:group-hover:bg-[#ef6351]/20',
    arrow: 'text-rose-600 group-hover:text-rose-700 dark:text-white/25 dark:group-hover:text-[#ffb0a6]',
  },
  cyan: {
    card: 'bg-sky-50/85 ring-sky-200/80 hover:bg-sky-100 hover:ring-sky-300 dark:bg-sky-500/[0.08] dark:ring-white/[0.08] dark:hover:bg-sky-500/[0.16] dark:hover:ring-sky-400/45',
    icon: 'bg-sky-100 text-sky-700 ring-sky-200/80 dark:bg-sky-500/10 dark:text-sky-200 dark:ring-sky-300/15 dark:group-hover:bg-sky-500/20',
    arrow: 'text-sky-600 group-hover:text-sky-700 dark:text-white/25 dark:group-hover:text-sky-200',
  },
  amber: {
    card: 'bg-amber-50/88 ring-amber-200/80 hover:bg-amber-100 hover:ring-amber-300 dark:bg-amber-500/[0.08] dark:ring-white/[0.08] dark:hover:bg-amber-500/[0.16] dark:hover:ring-amber-400/45',
    icon: 'bg-amber-100 text-amber-700 ring-amber-200/80 dark:bg-amber-500/10 dark:text-amber-200 dark:ring-amber-300/15 dark:group-hover:bg-amber-500/20',
    arrow: 'text-amber-600 group-hover:text-amber-700 dark:text-white/25 dark:group-hover:text-amber-200',
  },
  violet: {
    card: 'bg-violet-50/85 ring-violet-200/80 hover:bg-violet-100 hover:ring-violet-300 dark:bg-violet-500/[0.08] dark:ring-white/[0.08] dark:hover:bg-violet-500/[0.16] dark:hover:ring-violet-400/45',
    icon: 'bg-violet-100 text-violet-700 ring-violet-200/80 dark:bg-violet-500/10 dark:text-violet-200 dark:ring-violet-300/15 dark:group-hover:bg-violet-500/20',
    arrow: 'text-violet-600 group-hover:text-violet-700 dark:text-white/25 dark:group-hover:text-violet-200',
  },
} satisfies Record<HomeHubAccent, Record<'card' | 'icon' | 'arrow', string>>;

const LAST_HOME_HUB_KEY = 'nexus_last_home_hub';

export default function HomePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAdmin, isPlatformAdmin, isSuperAdmin, canViewHub, canAccessApp, hasStructureScope } = useAuth();
  const { state: maintenanceState } = useMaintenance();
  const legacyFallbackEnabled = import.meta.env.VITE_ALLOW_LEGACY_ADMIN_FALLBACK !== 'false';
  const canAccessAdminArea = useCallback((requiredArea?: string) => canAccessPlatformAdmin({
    platformAccessVerified: user?.platformAccessVerified,
    isSuperAdmin,
    isPlatformAdmin,
    platformAdminAreas: user?.platformAdminAreas,
    isAdmin,
    isLegacyAdmin: user?.isLegacyAdmin,
    legacyFallbackEnabled,
    requiredArea,
  }), [user?.platformAccessVerified, user?.platformAdminAreas, user?.isLegacyAdmin, isSuperAdmin, isPlatformAdmin, isAdmin, legacyFallbackEnabled]);

  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => 
    localStorage.getItem('sidebar_collapsed') === 'true'
  );
  const [supportModalOpen, setSupportModalOpen] = useState(false);
  const [guideModalOpen, setGuideModalOpen] = useState(false);
  const [shortcutHelpOpen, setShortcutHelpOpen] = useState(false);
  const { theme, toggleMode } = useTheme();
  const { success: showSuccessToast, error: showErrorToast } = useToast();
  const releaseNotesAccess = useMemo(() => ({
    permissions: user?.accessControlPermissions ?? [],
    isPlatformAdmin,
    isSuperAdmin: isSuperAdmin && Boolean(user?.platformAccessVerified),
  }), [isPlatformAdmin, isSuperAdmin, user?.accessControlPermissions, user?.platformAccessVerified]);
  const releaseNotes = useReleaseNotes({
    uid: user?.uid ?? null,
    ready: Boolean(user?.isAuthorized && user.accessControlPermissions),
    access: releaseNotesAccess,
  });
  const pendingShortcutRef = useRef<string | null>(null);
  const pendingShortcutTimerRef = useRef<number | null>(null);
  const shouldReduceMotion = useReducedMotion();
  const [lastHomeHub, setLastHomeHub] = useState<HomeHubId | null>(() => {
    const savedHub = localStorage.getItem(LAST_HOME_HUB_KEY);
    return homeHubs.some((hub) => hub.id === savedHub) ? savedHub as HomeHubId : null;
  });

  // Listener per sincronizzazione questionari in background
  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key !== 'icf_sync_result') return;
      if (!e.newValue) return;
      try {
        const result = JSON.parse(e.newValue);
        if (result.type === 'success') {
          showSuccessToast('Sincronizzazione completata', result.message);
        } else if (result.type === 'error') {
          showErrorToast('Errore sincronizzazione', result.message);
        }
        localStorage.removeItem('icf_sync_result');
      } catch {
        // ignora
      }
    };
    window.addEventListener('storage', handleStorage);
    // Controlla anche al mount se c'è un risultato pendente (stessa tab)
    const pending = localStorage.getItem('icf_sync_result');
    if (pending) {
      try {
        const result = JSON.parse(pending);
        if (result.type === 'success') {
          showSuccessToast('Sincronizzazione completata', result.message);
        } else if (result.type === 'error') {
          showErrorToast('Errore sincronizzazione', result.message);
        }
        localStorage.removeItem('icf_sync_result');
      } catch {
        // ignora
      }
    }
    return () => window.removeEventListener('storage', handleStorage);
  }, [showSuccessToast, showErrorToast]);

  // Seleziona un messaggio random ad ogni visita della home
  const [randomMessage] = useState(() => {
    return welcomeMessages[Math.floor(Math.random() * welcomeMessages.length)];
  });

  // Determine current section from URL path
  const normalizedPathname = normalizeMaintenancePathname(location.pathname);
  const isHome = normalizedPathname === '/';
  const currentSection = isHome ? 'home' : normalizedPathname.slice(1);
  const currentMaintenanceHub = resolveMaintenanceHub(normalizedPathname);

  useEffect(() => {
    if (isHome) return;
    const homeHub = homeHubs.find((hub) => hub.id === currentMaintenanceHub);
    if (!homeHub) return;
    localStorage.setItem(LAST_HOME_HUB_KEY, homeHub.id);
    setLastHomeHub(homeHub.id);
  }, [currentMaintenanceHub, isHome]);

  const [dependenciesMenuOpen, setDependenciesMenuOpen] = useState(() => currentMaintenanceHub === 'dipendenze');
  const [businessMenuOpen, setBusinessMenuOpen] = useState(() => currentMaintenanceHub === 'business');
  const [documentiMenuOpen, setDocumentiMenuOpen] = useState(() => currentMaintenanceHub === 'documenti');
  const [hrMenuOpen, setHrMenuOpen] = useState(() => currentMaintenanceHub === 'hr');
  const [adminMenuOpen, setAdminMenuOpen] = useState(() => isAdminRoute(normalizedPathname));

  useEffect(() => {
    if (currentMaintenanceHub === 'dipendenze') setDependenciesMenuOpen(true);
    if (currentMaintenanceHub === 'business') setBusinessMenuOpen(true);
    if (currentMaintenanceHub === 'documenti') setDocumentiMenuOpen(true);
    if (currentMaintenanceHub === 'hr') setHrMenuOpen(true);
    if (isAdminRoute(normalizedPathname)) setAdminMenuOpen(true);
  }, [currentMaintenanceHub, normalizedPathname]);
  
  // Stato utente autenticato
  const [userEmail, setUserEmail] = useState<string | null>(auth.currentUser?.email ?? null);
  const [userName, setUserName] = useState<string | null>(auth.currentUser?.displayName ?? null);
  const [userPhoto, setUserPhoto] = useState<string | null>(auth.currentUser?.photoURL ?? null);
  
  // Conteggio notifiche non lette
  const [unreadNotifications, setUnreadNotifications] = useState(0);

  // Fetch notifiche non lette
  const fetchUnreadCount = useCallback(async () => {
    try {
      const user = auth.currentUser;
      if (!user) return;
      
      const token = await user.getIdToken();
      const response = await fetch(`${ADMIN_SERVICE_URL}/notifications/count`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUnreadNotifications(data.result?.unreadCount || 0);
      }
    } catch (error) {
      console.error('Errore fetch notifiche:', error);
    }
  }, []);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUserEmail(u?.email ?? null);
      setUserName(u?.displayName ?? null);
      setUserPhoto(u?.photoURL ?? null);
      
      // Fetch notifiche quando l'utente è autenticato
      if (u) {
        fetchUnreadCount();
      }
    });
    return () => unsub();
  }, [fetchUnreadCount]);
  
  // Refresh notifiche ogni 60 secondi
  useEffect(() => {
    if (!userEmail) return;
    
    const interval = setInterval(fetchUnreadCount, 60000);
    return () => clearInterval(interval);
  }, [userEmail, fetchUnreadCount]);

  const toggleSidebar = () => {
    setSidebarCollapsed((prev) => !prev);
  };

  useEffect(() => {
    localStorage.setItem('sidebar_collapsed', sidebarCollapsed.toString());
    document.documentElement.style.setProperty(
      '--sidebar-width',
      sidebarCollapsed ? '4rem' : '16rem'
    );
  }, [sidebarCollapsed]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const isTypingTarget =
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement ||
        target?.isContentEditable;

      if (isTypingTarget) return;

      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'b') {
        e.preventDefault();
        setSidebarCollapsed((prev) => !prev);
        return;
      }

      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setShortcutHelpOpen(true);
        return;
      }

      if (e.key === '?') {
        e.preventDefault();
        setShortcutHelpOpen(true);
        return;
      }

      if (pendingShortcutRef.current === 'g') {
        const key = e.key.toLowerCase();
        pendingShortcutRef.current = null;
        if (pendingShortcutTimerRef.current) {
          window.clearTimeout(pendingShortcutTimerRef.current);
          pendingShortcutTimerRef.current = null;
        }

        if (key === 'h') {
          e.preventDefault();
          navigate('/');
          return;
        }

        if (key === 'b') {
          e.preventDefault();
          navigate('/business');
          return;
        }

        if (key === 'd') {
          e.preventDefault();
          navigate('/dipendenze');
          return;
        }

        if (key === 'o' && isAdmin && DOCUMENTS_HUB_ENABLED) {
          e.preventDefault();
          navigate('/documents');
          return;
        }
      }

      if (!e.metaKey && !e.ctrlKey && !e.altKey && e.key.toLowerCase() === 'g') {
        pendingShortcutRef.current = 'g';
        if (pendingShortcutTimerRef.current) {
          window.clearTimeout(pendingShortcutTimerRef.current);
        }
        pendingShortcutTimerRef.current = window.setTimeout(() => {
          pendingShortcutRef.current = null;
          pendingShortcutTimerRef.current = null;
        }, 1200);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      if (pendingShortcutTimerRef.current) {
        window.clearTimeout(pendingShortcutTimerRef.current);
      }
    };
  }, [isAdmin, navigate]);

  const initials = useMemo(() => {
    const src = userName || userEmail || '';
    if (!src) return 'U';
    const parts = src.replace(/@.*/, '').split(/[ ._-]/).filter(Boolean);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }, [userName, userEmail]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login', { replace: true });
    } catch (e) {
      console.error('Errore durante il logout:', e);
    }
  };

  const visibleHomeHubs = getVisibleHomeHubs(homeHubs, canViewHub);
  const resumeHub = visibleHomeHubs.find((hub) => hub.id === lastHomeHub) ?? visibleHomeHubs[0];
  const formattedDate = new Date().toLocaleDateString('it-IT', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  const fallbackContent = (
    <div className="relative flex min-h-[calc(100dvh-3.5rem)] w-full overflow-hidden bg-[#f7f8f7] text-zinc-950 dark:bg-[#090d10] dark:text-white lg:min-h-[calc(100dvh-0.5rem)]">
      <img
        src="/home-background.jpg"
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 h-full w-full object-cover object-center opacity-[0.10] dark:opacity-35"
      />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-[#f7f8f7] via-[#f7f8f7]/95 to-[#e9eeec]/78 dark:from-[#090d10] dark:via-[#090d10]/90 dark:to-[#090d10]/45" />
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_78%_18%,rgba(48,199,183,0.14),transparent_40%)] dark:bg-[radial-gradient(circle_at_78%_18%,rgba(48,199,183,0.12),transparent_34%)]" />

      <div className="relative flex w-full flex-1 flex-col">
        <motion.header
          initial={shouldReduceMotion ? false : { opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="mx-auto flex w-full max-w-[1440px] items-start justify-between gap-5 px-5 pb-5 pt-6 sm:px-8 sm:pt-8 lg:px-10 xl:px-12"
        >
          <div className="min-w-0">
            <p className="text-xs font-medium capitalize tracking-[0.08em] text-zinc-500 dark:text-white/55">
              {formattedDate}
            </p>
            <h1 className="mt-3 text-4xl font-light tracking-[-0.045em] text-zinc-950 dark:text-white sm:text-5xl">
              {randomMessage.greeting}, <span className="font-semibold">{userName?.split(' ')[0] || 'Utente'}</span>
            </h1>
            <p className="mt-3 max-w-[62ch] text-sm leading-relaxed text-zinc-600 dark:text-white/68 sm:text-base">
              {randomMessage.message}
            </p>
          </div>

          <button
            type="button"
            onClick={() => setShortcutHelpOpen(true)}
            className="group hidden shrink-0 items-center gap-3 rounded-xl bg-white/75 px-3 py-2.5 text-sm font-medium text-zinc-700 ring-1 ring-inset ring-zinc-950/[0.08] dark:bg-white/[0.06] dark:text-white/75 dark:ring-white/10 transition duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] hover:-translate-y-0.5 hover:bg-white hover:text-zinc-950 dark:hover:bg-white/[0.10] dark:hover:text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#30c7b7] motion-reduce:transform-none motion-reduce:transition-none sm:flex"
            aria-label="Apri le scorciatoie da tastiera"
          >
            <CommandLineIcon className="h-4 w-4 text-[#56d4c6]" aria-hidden="true" />
            <span>Scorciatoie</span>
            <kbd className="rounded-md bg-zinc-100 px-2 py-1 text-[11px] font-semibold text-zinc-500 ring-1 ring-inset ring-zinc-950/[0.08] dark:bg-black/25 dark:text-white/55 dark:ring-white/10">
              Ctrl K
            </kbd>
          </button>
        </motion.header>

        <main className="mx-auto flex w-full max-w-[1440px] flex-1 flex-col justify-center px-5 pb-7 sm:px-8 sm:pb-9 lg:px-10 xl:px-12">
          <div className="grid w-full gap-4 xl:grid-cols-12">
            <motion.section
              initial={shouldReduceMotion ? false : { opacity: 0, y: 22 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.75, delay: shouldReduceMotion ? 0 : 0.08, ease: [0.16, 1, 0.3, 1] }}
              className="relative flex min-h-[20rem] flex-col justify-between overflow-hidden rounded-2xl bg-white/88 p-6 shadow-[0_18px_50px_rgba(7,88,76,0.08)] ring-1 ring-inset ring-zinc-950/[0.08] dark:bg-[#101719] dark:shadow-none dark:ring-white/10 sm:p-8 xl:col-span-7"
              aria-labelledby="resume-title"
            >
              <img
                src="/home-background.jpg"
                alt=""
                aria-hidden="true"
                className="pointer-events-none absolute inset-y-0 right-0 h-full w-[72%] object-cover object-center opacity-[0.10] dark:opacity-55"
              />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-white via-white/94 to-teal-50/35 dark:from-[#101719] dark:via-[#101719]/95 dark:to-[#101719]/20" />

              <div className="relative max-w-xl">
                <div className="inline-flex items-center gap-2 text-sm font-medium text-[#73ded2]">
                  <ClockIcon className="h-4 w-4" aria-hidden="true" />
                  <span>{lastHomeHub && resumeHub?.id === lastHomeHub ? 'Riprendi il lavoro' : 'Inizia da qui'}</span>
                </div>
                <h2 id="resume-title" className="mt-6 max-w-lg text-4xl font-semibold tracking-[-0.045em] text-zinc-950 dark:text-white sm:text-5xl">
                  {resumeHub?.title ?? 'Il tuo spazio di lavoro'}
                </h2>
                <p className="mt-4 max-w-[50ch] text-sm leading-relaxed text-zinc-600 dark:text-white/62 sm:text-base">
                  {resumeHub?.description ?? 'Non risultano ancora aree disponibili per il tuo profilo.'}
                </p>
              </div>

              {resumeHub && (
                <button
                  type="button"
                  onClick={() => navigate(resumeHub.path)}
                  className="group relative mt-8 flex w-fit items-center gap-3 rounded-full bg-[#dffbf7] py-1.5 pl-5 pr-1.5 text-sm font-semibold text-[#09211e] transition duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] hover:-translate-y-0.5 hover:bg-white focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#73ded2] active:scale-[0.98] motion-reduce:transform-none motion-reduce:transition-none"
                >
                  <span>Apri {resumeHub.title}</span>
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#123d37] text-white transition duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 motion-reduce:transform-none">
                    <ArrowUpRightIcon className="h-4 w-4" aria-hidden="true" />
                  </span>
                </button>
              )}
            </motion.section>

            <motion.section
              initial={shouldReduceMotion ? false : { opacity: 0, y: 22 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.75, delay: shouldReduceMotion ? 0 : 0.14, ease: [0.16, 1, 0.3, 1] }}
              className="flex min-h-[20rem] flex-col rounded-2xl bg-white/82 p-5 shadow-[0_18px_50px_rgba(7,88,76,0.07)] ring-1 ring-inset ring-zinc-950/[0.08] dark:bg-[#101719]/95 dark:shadow-none dark:ring-white/10 sm:p-6 xl:col-span-5"
              aria-labelledby="home-areas-title"
            >
              <div className="mb-5 flex items-end justify-between gap-4">
                <div>
                  <h2 id="home-areas-title" className="text-xl font-semibold tracking-[-0.025em] text-zinc-950 dark:text-white">Le tue aree</h2>
                  <p className="mt-1 text-sm text-zinc-600 dark:text-white/50">Accessi disponibili per il tuo profilo</p>
                </div>
                <span className="shrink-0 text-sm tabular-nums text-zinc-500 dark:text-white/60">{visibleHomeHubs.length}</span>
              </div>

              {visibleHomeHubs.length > 0 ? (
                <div className="grid flex-1 grid-cols-1 gap-3 sm:grid-cols-2">
                  {visibleHomeHubs.map((hub, index) => {
                    const HubIcon = hub.icon;
                    const fillsLastRow = visibleHomeHubs.length % 2 === 1 && index === visibleHomeHubs.length - 1;
                    return (
                      <button
                        key={hub.id}
                        type="button"
                        onClick={() => navigate(hub.path)}
                        className={`group relative min-h-32 overflow-hidden rounded-2xl p-4 text-left transition duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] hover:-translate-y-1 ${homeHubStyles[hub.accent].card} focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#30c7b7] active:scale-[0.98] motion-reduce:transform-none motion-reduce:transition-none ${fillsLastRow ? 'sm:col-span-2' : ''}`}
                      >
                        <div className="flex h-full flex-col">
                          <div className="flex items-start justify-between gap-3">
                            <span className={`flex h-10 w-10 items-center justify-center rounded-xl transition duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] ${homeHubStyles[hub.accent].icon}`}>
                              <HubIcon className="h-5 w-5" aria-hidden="true" />
                            </span>
                            <ArrowUpRightIcon className={`h-4 w-4 transition duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 motion-reduce:transform-none ${homeHubStyles[hub.accent].arrow}`} aria-hidden="true" />
                          </div>
                          <div className="mt-auto pt-4">
                            <h3 className="text-sm font-semibold text-zinc-950 dark:text-white">{hub.title}</h3>
                            <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-zinc-600 dark:text-white/58">{hub.description}</p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="flex flex-1 items-center rounded-2xl bg-teal-50/60 p-5 text-sm leading-relaxed text-zinc-600 ring-1 ring-inset ring-zinc-950/[0.07] dark:bg-white/[0.035] dark:text-white/55 dark:ring-white/[0.07]">
                  Contatta un amministratore per richiedere l'accesso a un'area di lavoro.
                </div>
              )}
            </motion.section>
          </div>

          <motion.footer
            initial={shouldReduceMotion ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: shouldReduceMotion ? 0 : 0.22 }}
            className="mt-5 flex flex-col gap-2 text-xs text-zinc-500 sm:flex-row sm:items-center sm:justify-between dark:text-white/55"
          >
            <span>Polo9 Società Cooperativa Sociale</span>
            <span>{visibleHomeHubs.length === 1 ? '1 area disponibile' : `${visibleHomeHubs.length} aree disponibili`}</span>
          </motion.footer>
        </main>
      </div>
    </div>
  );

  if (!isHome && !isKnownHomeSection(currentSection)) {
    return <NotFound />;
  }

  return (
    <SidebarLayout
      navbar={
        <Navbar>
          <NavbarSpacer />
        </Navbar>
      }
      sidebar={
        <AppSidebar
          adminMenuOpen={adminMenuOpen}
          businessMenuOpen={businessMenuOpen}
          currentSection={currentSection}
          dependenciesMenuOpen={dependenciesMenuOpen}
          documentiMenuOpen={documentiMenuOpen}
          hrMenuOpen={hrMenuOpen}
          initials={initials}
          canAccessAdminArea={canAccessAdminArea}
          maintenanceHubs={maintenanceState.hubs}
          sidebarCollapsed={sidebarCollapsed}
          themeMode={theme.mode}
          unreadNotifications={unreadNotifications}
          userEmail={userEmail}
          userName={userName}
          userPhoto={userPhoto}
          permissions={user?.accessControlPermissions ?? []}
          canAccessApp={canAccessApp}
          hasStructureScope={hasStructureScope}
          canViewHub={canViewHub}
          navigate={navigate}
          onLogout={handleLogout}
          onOpenReleaseNotes={() => releaseNotes.openRelease()}
          onOpenGuide={() => setGuideModalOpen(true)}
          onOpenSupport={() => setSupportModalOpen(true)}
          releaseNotesUnread={releaseNotes.isCurrentReleaseUnread}
          releaseNotesVersion={releaseNotes.currentVersion}
          onToggleMode={toggleMode}
          onToggleSidebar={toggleSidebar}
          onToggleDependenciesMenu={() => setDependenciesMenuOpen(prev => !prev)}
          onToggleBusinessMenu={() => setBusinessMenuOpen(prev => !prev)}
          onToggleDocumentiMenu={() => setDocumentiMenuOpen(prev => !prev)}
          onToggleHRMenu={() => setHrMenuOpen(prev => !prev)}
          onToggleAdminMenu={() => setAdminMenuOpen(prev => !prev)}
        />
      }
    >
      <div className="min-h-full">
        {renderHomeContent({ currentSection, fallbackContent, isHome })}
      </div>
      <DebugPanel />
      
      {/* Support Modal */}
      <SupportModal
        isOpen={supportModalOpen}
        onClose={() => setSupportModalOpen(false)}
        userEmail={userEmail}
        userName={userName}
      />
      <GuideModal
        isOpen={guideModalOpen}
        onClose={() => setGuideModalOpen(false)}
      />
      <ShortcutHelpModal
        isAdmin={isAdmin}
        isOpen={shortcutHelpOpen}
        onClose={() => setShortcutHelpOpen(false)}
      />
      <ReleaseNotesCenter
        open={releaseNotes.isOpen}
        availableReleases={releaseNotes.visibleReleaseNotes}
        selectedVersion={releaseNotes.selectedRelease?.version ?? null}
        currentVersion={releaseNotes.currentVersion}
        onClose={releaseNotes.closeReleaseNotes}
        onSelectRelease={releaseNotes.openRelease}
        onMarkCurrentReleaseAsRead={releaseNotes.markCurrentReleaseAsRead}
        onStartCurrentRelease={releaseNotes.startCurrentRelease}
      />
    </SidebarLayout>
  );
}
